import { InteractiveLiveBrowserSession } from '@/app/chat/_common/interactive-live-browser-session'
import { StepStatusIcon } from '@/app/chat/_common/plan-steps/step-status'
import { PlanStepTaskItems } from '@/app/chat/_common/plan-steps/task-items'
import { useTurnStepParsed } from '@/app/chat/_common/use-turn-parsed'
import { TurnStatus } from '@/hooks/api/use-turn-status-query'
import React from 'react'
import { type TabTurnStep } from '../types'
import { ErrorTabContent } from './_templates'

// =============================================================================
// STEP TAB CONTENT COMPONENTS
// =============================================================================

/**
 * Content for session video tabs - displays video recordings or live session views
 */
export const RecordedSessionTabContent = ({ tab }: Readonly<{ tab: TabTurnStep }>) => {
  const parsedStep = useTurnStepParsed({
    turnId: tab.turnId,
    stepId: tab.stepId,
    workflowId: tab.workflowId,
  })
  const recordingUrl = parsedStep?.outputDisplayData.recordingUrl

  if (!parsedStep) {
    return null
  }

  return (
    <SessionTabTemplate
      status={parsedStep?.status}
      description={parsedStep?.outputDisplayData.description}
    >
      {recordingUrl ? (
        <video
          src={recordingUrl}
          muted
          playsInline
          autoPlay={false}
          controls
          className='aspect-[1279/719] w-full'
        />
      ) : (
        <div className='aspect-[1279/719] w-full'>
          <ErrorTabContent />
        </div>
      )}
    </SessionTabTemplate>
  )
}

export const LiveSessionTabContent = ({ tab }: Readonly<{ tab: TabTurnStep }>) => (
  <InteractiveLiveBrowserSession
    workflowId={tab.workflowId}
    turnId={tab.turnId}
    stepId={tab.stepId}
  />
)

export const SessionTabTemplate = ({
  status,
  description,
  footer,
  children,
}: React.PropsWithChildren<{
  status?: TurnStatus
  description?: string
  footer?: React.ReactNode
}>) => (
  <div className='flex w-full flex-col items-center gap-6'>
    <div className='flex w-full flex-col overflow-hidden rounded-lg border border-neutral-75 bg-neutral-25'>
      <div className='flex items-center gap-2 p-3 text-sm font-normal'>
        {status && <StepStatusIcon status={status} />}
        {description}
      </div>
      {children}
    </div>
    {footer}
  </div>
)

/**
 * Content for step updates tabs - displays task updates and progress
 */
export const StepUpdatesTabContent = ({ tab }: Readonly<{ tab: TabTurnStep }>) => {
  const parsedStep = useTurnStepParsed({
    turnId: tab.turnId,
    stepId: tab.stepId,
    workflowId: tab.workflowId,
  })
  const tasks = parsedStep?.outputDisplayData.tasks ?? []
  return (
    <div className='flex w-full flex-col gap-4'>
      <div className='text-lg'>{tab.title}</div>
      <PlanStepTaskItems tasks={tasks} />
    </div>
  )
}
